import {
  AlertTriangle,
  BarChart3,
  CheckCircle2,
  ClipboardCopy,
  FileSearch,
  Gavel,
  Loader2,
  Scale,
  Search,
  Send,
  ShieldCheck,
  Trash2,
} from "lucide-react";
import { FormEvent, useEffect, useMemo, useState } from "react";
import { createFitReport } from "../shared/scoring";
import {
  categoryLabels,
  stageLabels,
  type CaseCategory,
  type CaseProfile,
  type FitReport,
  type LawyerProfile,
  type ProcedureStage,
} from "../shared/types";

const defaultCaseProfile: CaseProfile = {
  category: "fraud_money_laundering",
  courtRegion: "臺灣臺北地方法院",
  stage: "first_instance",
  objective: "確認候選律師是否熟悉詐欺、洗錢與一審攻防，並準備面談問題。",
  detained: false,
  priorRecord: false,
  selectedLawyerIds: ["chen-bo-fu", "lin-pin-an-demo", "wang-cheng-yue-demo"],
};

type ReportManagement = {
  token: string;
  expiresAt: string;
  header: string;
};

type CorrectionForm = {
  lawyerId: string;
  requesterEmail: string;
  requestType: "wrong_identity" | "wrong_role" | "missing_context" | "delete_private_data" | "other";
  message: string;
  evidenceUrl: string;
};

export function App() {
  const [lawyers, setLawyers] = useState<LawyerProfile[]>([]);
  const [query, setQuery] = useState("");
  const [caseProfile, setCaseProfile] = useState<CaseProfile>(defaultCaseProfile);
  const [selectedIds, setSelectedIds] = useState<string[]>(defaultCaseProfile.selectedLawyerIds ?? []);
  const [report, setReport] = useState<FitReport | null>(null);
  const [reportManagement, setReportManagement] = useState<ReportManagement | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isDeletingReport, setIsDeletingReport] = useState(false);
  const [reportActionNotice, setReportActionNotice] = useState("");
  const [apiNotice, setApiNotice] = useState("正在連線 API");
  const [correctionForm, setCorrectionForm] = useState<CorrectionForm>({
    lawyerId: defaultCaseProfile.selectedLawyerIds?.[0] ?? "",
    requesterEmail: "",
    requestType: "missing_context",
    message: "",
    evidenceUrl: "",
  });
  const [isSubmittingCorrection, setIsSubmittingCorrection] = useState(false);
  const [correctionNotice, setCorrectionNotice] = useState("");

  useEffect(() => {
    void loadLawyers("");
  }, []);

  const selectedLawyers = useMemo(
    () => lawyers.filter((lawyer) => selectedIds.includes(lawyer.id)),
    [lawyers, selectedIds],
  );

  async function loadLawyers(search: string) {
    try {
      const response = await fetch(`/api/lawyers?q=${encodeURIComponent(search)}`);
      if (!response.ok) throw new Error(`API ${response.status}`);
      const payload = (await response.json()) as { data: LawyerProfile[] };
      setLawyers(payload.data);
      setApiNotice("API 已連線，資料模式：seed fallback");
    } catch {
      const fallback = createFitReport(defaultCaseProfile).results.map((item) => item.lawyer);
      setLawyers(fallback);
      setApiNotice("API 尚未啟動，前端使用內建示範資料");
    }
  }

  async function submitReport(event: FormEvent) {
    event.preventDefault();
    setIsLoading(true);
    const request: CaseProfile = { ...caseProfile, selectedLawyerIds: selectedIds };
    try {
      const response = await fetch("/api/reports", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(request),
      });
      if (!response.ok) throw new Error(`API ${response.status}`);
      const payload = (await response.json()) as { data: FitReport; management?: ReportManagement };
      setReport(payload.data);
      setReportManagement(payload.management ?? null);
      setReportActionNotice(payload.management ? "報告已建立，可用管理 token 讀取或刪除。" : "");
    } catch {
      setReport(createFitReport(request));
      setReportManagement(null);
      setReportActionNotice("");
      setApiNotice("API 回應失敗，已用前端本機模型產生報告");
    } finally {
      setIsLoading(false);
    }
  }

  function updateCase<K extends keyof CaseProfile>(key: K, value: CaseProfile[K]) {
    setCaseProfile((current) => ({ ...current, [key]: value }));
  }

  function toggleLawyer(id: string) {
    setSelectedIds((current) =>
      current.includes(id) ? current.filter((item) => item !== id) : [...current, id].slice(-3),
    );
    setCorrectionForm((current) => ({ ...current, lawyerId: id }));
  }

  async function copyReportToken() {
    if (!reportManagement) return;
    await navigator.clipboard?.writeText(reportManagement.token);
    setReportActionNotice("管理 token 已複製。");
  }

  async function deleteReport() {
    if (!report || !reportManagement) return;
    if (!confirm("確定刪除此報告？刪除後將無法用管理 token 讀取。")) return;

    setIsDeletingReport(true);
    try {
      const response = await fetch("/api/reports", {
        method: "DELETE",
        headers: { [reportManagement.header]: reportManagement.token },
      });
      if (!response.ok) throw new Error(`API ${response.status}`);
      setReport(null);
      setReportManagement(null);
      setReportActionNotice("報告已刪除。");
    } catch {
      setReportActionNotice("刪除失敗，請稍後再試。");
    } finally {
      setIsDeletingReport(false);
    }
  }

  async function submitCorrection(event: FormEvent) {
    event.preventDefault();
    setIsSubmittingCorrection(true);
    setCorrectionNotice("");
    const payload = {
      lawyerId: correctionForm.lawyerId || undefined,
      requesterEmail: correctionForm.requesterEmail,
      requestType: correctionForm.requestType,
      message: correctionForm.message,
      evidenceUrl: correctionForm.evidenceUrl || undefined,
    };

    try {
      const response = await fetch("/api/correction-requests", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!response.ok) throw new Error(`API ${response.status}`);
      const data = (await response.json()) as { data: { id: string; status: string } };
      setCorrectionNotice(`已送出勘誤申請：${data.data.status}`);
      setCorrectionForm((current) => ({ ...current, message: "", evidenceUrl: "" }));
    } catch {
      setCorrectionNotice("勘誤申請送出失敗，請確認 API 與資料庫狀態。");
    } finally {
      setIsSubmittingCorrection(false);
    }
  }

  return (
    <main className="shell">
      <header className="topbar">
        <div className="brand">
          <div className="brand-mark">
            <Scale size={22} />
          </div>
          <div>
            <h1>AI 律師適配分析器</h1>
            <p>用公開裁判書脈絡，比較候選律師和案件需求是否匹配。</p>
          </div>
        </div>
        <div className="status-pill">
          <ShieldCheck size={16} />
          <span>{apiNotice}</span>
        </div>
      </header>

      <section className="workspace">
        <form className="panel case-panel" onSubmit={submitReport}>
          <div className="panel-heading">
            <Gavel size={19} />
            <h2>案件條件</h2>
          </div>

          <label>
            案件類型
            <select
              value={caseProfile.category}
              onChange={(event) => updateCase("category", event.target.value as CaseCategory)}
            >
              {Object.entries(categoryLabels).map(([key, label]) => (
                <option key={key} value={key}>
                  {label}
                </option>
              ))}
            </select>
          </label>

          <label>
            法院或地區
            <input
              value={caseProfile.courtRegion}
              onChange={(event) => updateCase("courtRegion", event.target.value)}
              placeholder="例如：臺灣臺北地方法院"
            />
          </label>

          <label>
            程序階段
            <select value={caseProfile.stage} onChange={(event) => updateCase("stage", event.target.value as ProcedureStage)}>
              {Object.entries(stageLabels).map(([key, label]) => (
                <option key={key} value={key}>
                  {label}
                </option>
              ))}
            </select>
          </label>

          <div className="toggle-row">
            <label className="check-label">
              <input
                type="checkbox"
                checked={caseProfile.detained}
                onChange={(event) => updateCase("detained", event.target.checked)}
              />
              涉及羈押／具保
            </label>
            <label className="check-label">
              <input
                type="checkbox"
                checked={caseProfile.priorRecord}
                onChange={(event) => updateCase("priorRecord", event.target.checked)}
              />
              有前科或累犯疑慮
            </label>
          </div>

          <label>
            目標
            <textarea value={caseProfile.objective} onChange={(event) => updateCase("objective", event.target.value)} rows={4} />
          </label>

          <button className="primary-button" type="submit" disabled={isLoading || selectedIds.length === 0}>
            {isLoading ? <Loader2 className="spin" size={18} /> : <FileSearch size={18} />}
            產生適配報告
          </button>
        </form>

        <section className="panel lawyers-panel">
          <div className="panel-heading split">
            <div>
              <div className="heading-inline">
                <Search size={19} />
                <h2>候選律師</h2>
              </div>
              <p>最多選 3 位比較。</p>
            </div>
            <span className="count">{selectedIds.length}/3</span>
          </div>

          <div className="searchbox">
            <Search size={16} />
            <input
              value={query}
              onChange={(event) => {
                setQuery(event.target.value);
                void loadLawyers(event.target.value);
              }}
              placeholder="搜尋姓名、事務所、地區、案由"
            />
          </div>

          <div className="lawyer-list">
            {lawyers.map((lawyer) => (
              <button
                key={lawyer.id}
                className={`lawyer-card ${selectedIds.includes(lawyer.id) ? "selected" : ""}`}
                type="button"
                onClick={() => toggleLawyer(lawyer.id)}
              >
                <div>
                  <h3>{lawyer.name}</h3>
                  <p>{lawyer.officeName}</p>
                </div>
                <div className="lawyer-meta">
                  <span>{lawyer.location}</span>
                  <span>{lawyer.stats.totalPublicCriminalRoles} 筆刑事角色</span>
                  <span>{lawyer.stats.recentRoleCount} 筆近期</span>
                </div>
                <CategoryBars profile={lawyer} />
              </button>
            ))}
          </div>

          <form className="correction-box" onSubmit={submitCorrection}>
            <div className="panel-heading compact">
              <AlertTriangle size={17} />
              <h2>資料勘誤</h2>
            </div>
            <label>
              對象
              <select
                value={correctionForm.lawyerId}
                onChange={(event) => setCorrectionForm((current) => ({ ...current, lawyerId: event.target.value }))}
              >
                <option value="">未指定律師</option>
                {lawyers.map((lawyer) => (
                  <option key={lawyer.id} value={lawyer.id}>
                    {lawyer.name}
                  </option>
                ))}
              </select>
            </label>
            <label>
              Email
              <input
                value={correctionForm.requesterEmail}
                onChange={(event) => setCorrectionForm((current) => ({ ...current, requesterEmail: event.target.value }))}
                placeholder="reviewer@example.com"
              />
            </label>
            <label>
              類型
              <select
                value={correctionForm.requestType}
                onChange={(event) =>
                  setCorrectionForm((current) => ({
                    ...current,
                    requestType: event.target.value as CorrectionForm["requestType"],
                  }))
                }
              >
                <option value="missing_context">補充脈絡</option>
                <option value="wrong_identity">同名誤判</option>
                <option value="wrong_role">角色錯誤</option>
                <option value="delete_private_data">刪除私人資料</option>
                <option value="other">其他</option>
              </select>
            </label>
            <label>
              說明
              <textarea
                value={correctionForm.message}
                onChange={(event) => setCorrectionForm((current) => ({ ...current, message: event.target.value }))}
                rows={3}
                placeholder="請描述需修正的公開資料與理由"
              />
            </label>
            <label>
              佐證連結
              <input
                value={correctionForm.evidenceUrl}
                onChange={(event) => setCorrectionForm((current) => ({ ...current, evidenceUrl: event.target.value }))}
                placeholder="https://"
              />
            </label>
            <button
              className="secondary-button"
              type="submit"
              disabled={isSubmittingCorrection || !correctionForm.requesterEmail || correctionForm.message.length < 10}
            >
              {isSubmittingCorrection ? <Loader2 className="spin" size={16} /> : <Send size={16} />}
              送出勘誤
            </button>
            {correctionNotice && <p className="action-notice">{correctionNotice}</p>}
          </form>
        </section>

        <section className="panel report-panel">
          <div className="panel-heading">
            <BarChart3 size={19} />
            <h2>分析報告</h2>
          </div>

          {!report ? (
            <div className="empty-report">
              <FileSearch size={34} />
              <h3>輸入案件條件後產生報告</h3>
              <p>報告會顯示適配度、理由、相近公開案件、風險與面談問題。</p>
              <div className="selected-strip">
                {selectedLawyers.map((lawyer) => (
                  <span key={lawyer.id}>{lawyer.name}</span>
                ))}
              </div>
              {reportActionNotice && <p className="action-notice">{reportActionNotice}</p>}
            </div>
          ) : (
            <ReportView
              report={report}
              management={reportManagement}
              isDeleting={isDeletingReport}
              actionNotice={reportActionNotice}
              onCopyToken={copyReportToken}
              onDeleteReport={deleteReport}
            />
          )}
        </section>
      </section>
    </main>
  );
}

function CategoryBars({ profile }: { profile: LawyerProfile }) {
  const entries = Object.entries(profile.stats.categoryCounts).slice(0, 4);
  const max = Math.max(1, ...entries.map(([, count]) => count));
  return (
    <div className="bars">
      {entries.map(([category, count]) => (
        <div className="bar-line" key={category}>
          <span>{categoryLabels[category as CaseCategory]}</span>
          <div>
            <i style={{ width: `${Math.max(10, (count / max) * 100)}%` }} />
          </div>
        </div>
      ))}
    </div>
  );
}

function ReportView({
  report,
  management,
  isDeleting,
  actionNotice,
  onCopyToken,
  onDeleteReport,
}: {
  report: FitReport;
  management: ReportManagement | null;
  isDeleting: boolean;
  actionNotice: string;
  onCopyToken: () => void;
  onDeleteReport: () => void;
}) {
  return (
    <div className="report">
      <div className="report-summary">
        <CheckCircle2 size={20} />
        <p>{report.summary}</p>
      </div>

      {management && (
        <div className="report-management">
          <div>
            <span>管理權限</span>
            <strong>token 已建立</strong>
          </div>
          <div>
            <span>保留期限</span>
            <strong>{new Date(management.expiresAt).toLocaleDateString("zh-TW")}</strong>
          </div>
          <div className="report-actions">
            <button className="icon-button" type="button" onClick={onCopyToken} title="複製管理 token">
              <ClipboardCopy size={17} />
            </button>
            <button className="danger-button" type="button" onClick={onDeleteReport} disabled={isDeleting}>
              {isDeleting ? <Loader2 className="spin" size={16} /> : <Trash2 size={16} />}
              刪除報告
            </button>
          </div>
          {actionNotice && <p className="action-notice">{actionNotice}</p>}
        </div>
      )}

      <div className="result-list">
        {report.results.map((result) => (
          <article className="result-card" key={result.lawyer.id}>
            <div className="score-block">
              <strong>{result.totalScore}</strong>
              <span>{result.fitLevel === "high" ? "高度適配" : result.fitLevel === "medium" ? "中度適配" : "低度適配"}</span>
            </div>
            <div className="result-body">
              <div className="result-title">
                <h3>{result.lawyer.name}</h3>
                <p>{result.lawyer.officeName}</p>
              </div>

              <div className="dimensions">
                {result.dimensions.map((dimension) => (
                  <div className="dimension" key={dimension.key}>
                    <div>
                      <span>{dimension.label}</span>
                      <b>
                        {dimension.score}/{dimension.max}
                      </b>
                    </div>
                    <meter min={0} max={dimension.max} value={dimension.score} />
                    <p>{dimension.reason}</p>
                  </div>
                ))}
              </div>

              <div className="subsection">
                <h4>相近公開案件</h4>
                {result.matchingCases.length ? (
                  result.matchingCases.map((item) => (
                    <a className="source-row" href={item.sourceUrl} target="_blank" rel="noreferrer" key={item.id}>
                      <span>{item.date}</span>
                      <strong>{item.court}</strong>
                      <em>{item.cause}</em>
                    </a>
                  ))
                ) : (
                  <p className="muted">目前沒有足夠相近案件。</p>
                )}
              </div>

              <div className="two-column">
                <div className="subsection">
                  <h4>優勢</h4>
                  <ul>
                    {result.strengths.map((item) => (
                      <li key={item}>{item}</li>
                    ))}
                  </ul>
                </div>
                <div className="subsection">
                  <h4>待確認</h4>
                  <ul>
                    {result.cautions.map((item) => (
                      <li key={item}>{item}</li>
                    ))}
                  </ul>
                </div>
              </div>

              <div className="subsection">
                <h4>面談問題</h4>
                <ol>
                  {result.interviewQuestions.map((item) => (
                    <li key={item}>{item}</li>
                  ))}
                </ol>
              </div>
            </div>
          </article>
        ))}
      </div>

      <div className="disclaimer">
        <AlertTriangle size={18} />
        <p>{report.disclaimer}</p>
      </div>
    </div>
  );
}
